import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, AlertTriangle, ShieldAlert, Target, ClipboardList,
  Clock, ScanLine, Lock, Unlock, CheckCircle2, Camera, X,
  User, Calendar, Stethoscope,
} from 'lucide-react';
import { agendaOggi, fascicoli } from '@/data/mockOperatoreData';
import ClinicalDiaryForm from '@/pages/ClinicalDiaryForm';

const severitaColors = {
  alta: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800/50 text-red-800 dark:text-red-300',
  media: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-300',
  bassa: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-300',
};

const severitaIcon = {
  alta: ShieldAlert,
  media: AlertTriangle,
  bassa: CheckCircle2,
};

/* ─── QR Scan Modal ─── */
function QRScanModal({ isOpen, onClose, onSuccess }) {
  const [scanning, setScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);

  const handleSimulateScan = () => {
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
      setScanComplete(true);
      setTimeout(() => {
        onSuccess();
        onClose();
        setScanComplete(false);
      }, 1200);
    }, 2500);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="qr-scan-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-card rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
          data-testid="qr-scan-modal"
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="text-base font-semibold text-foreground">Scansione QR Paziente</h3>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-xl"
              onClick={() => { onClose(); setScanComplete(false); setScanning(false); }}
              data-testid="qr-modal-close-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Camera Viewport Simulation */}
          <div className="relative bg-gray-900 aspect-square max-h-[320px] flex items-center justify-center overflow-hidden">
            {/* Viewfinder frame */}
            <div className="absolute inset-8 border-2 border-white/30 rounded-2xl" />

            {/* Corner marks */}
            <div className="absolute top-7 left-7 w-8 h-8 border-t-3 border-l-3 border-white rounded-tl-lg" />
            <div className="absolute top-7 right-7 w-8 h-8 border-t-3 border-r-3 border-white rounded-tr-lg" />
            <div className="absolute bottom-7 left-7 w-8 h-8 border-b-3 border-l-3 border-white rounded-bl-lg" />
            <div className="absolute bottom-7 right-7 w-8 h-8 border-b-3 border-r-3 border-white rounded-br-lg" />

            {/* Scanning line animation */}
            {scanning && (
              <motion.div
                initial={{ top: '10%' }}
                animate={{ top: ['10%', '85%', '10%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute left-8 right-8 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent shadow-[0_0_12px_rgba(52,211,153,0.6)]"
              />
            )}

            {/* Center content */}
            {!scanning && !scanComplete && (
              <div className="text-center z-10">
                <Camera className="w-12 h-12 text-white/50 mx-auto mb-3" />
                <p className="text-white/70 text-sm">Inquadra il QR Code del paziente</p>
              </div>
            )}

            {scanning && (
              <div className="text-center z-10">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                >
                  <ScanLine className="w-12 h-12 text-emerald-400 mx-auto" />
                </motion.div>
                <p className="text-emerald-300 text-sm mt-3 font-medium">Scansione in corso...</p>
              </div>
            )}

            {scanComplete && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="text-center z-10"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-9 h-9 text-white" />
                </div>
                <p className="text-emerald-300 text-base font-semibold">QR Code Verificato!</p>
              </motion.div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6">
            {!scanning && !scanComplete && (
              <Button
                className="w-full h-14 rounded-2xl text-base font-semibold gap-3"
                onClick={handleSimulateScan}
                data-testid="simulate-scan-btn"
              >
                <ScanLine className="w-5 h-5" />
                Simula Scansione Riuscita
              </Button>
            )}
            {scanning && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Attendere il completamento della scansione...</p>
              </div>
            )}
            {scanComplete && (
              <div className="text-center">
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Identita paziente confermata. Sblocco in corso...</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─── Alert Banner ─── */
function AlertBanner({ alerts }) {
  if (!alerts || alerts.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="alert-banner-section">
      {alerts.map((alert) => {
        const Icon = severitaIcon[alert.severita] || AlertTriangle;
        return (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl border ${severitaColors[alert.severita]}`}
            data-testid={`alert-${alert.id}`}
          >
            <Icon className="w-5 h-5 shrink-0" />
            <span className="text-sm font-semibold">{alert.testo}</span>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ─── Diary Timeline ─── */
function DiaryEntry({ entry, isLast }) {
  return (
    <div className="flex gap-4" data-testid={`diary-entry-${entry.id}`}>
      {/* Timeline line + dot */}
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-xl bg-muted/60 border border-border/40 flex items-center justify-center shrink-0">
          <span className="text-[11px] font-bold text-muted-foreground">{entry.avatar}</span>
        </div>
        {!isLast && <div className="w-px flex-1 bg-border/50 mt-2" />}
      </div>
      {/* Content */}
      <div className={`flex-1 ${!isLast ? 'pb-5' : ''}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-foreground">{entry.operatore}</span>
          <Badge variant="outline" className="text-[10px] px-2 py-0.5 rounded-md bg-muted/40 text-muted-foreground border-border/40">
            {entry.qualifica}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <Clock className="w-3 h-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{entry.data}</span>
        </div>
        <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{entry.nota}</p>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export default function HealthWorkerPatientFile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [presenzaCertificata, setPresenzaCertificata] = useState(false);
  const [oraCertificazione, setOraCertificazione] = useState('');

  const visita = agendaOggi.find((v) => v.id === id);
  const fascicolo = fascicoli[id];

  if (!visita || !fascicolo) {
    return (
      <AppLayout role="operatore">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl max-w-md w-full">
            <CardContent className="p-10 text-center">
              <p className="text-lg font-semibold text-foreground">Visita non trovata</p>
              <Button variant="outline" className="mt-4 rounded-xl" onClick={() => navigate('/operatore')}>
                Torna all'Agenda
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const handleScanSuccess = () => {
    setPresenzaCertificata(true);
    setOraCertificazione(new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }));
  };

  return (
    <AppLayout role="operatore">
      <div className="space-y-5" data-testid="patient-file-page">
        {/* Top: Back + Patient Identity */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-xl border border-border/40 shrink-0"
            onClick={() => navigate('/operatore')}
            data-testid="back-to-agenda-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-primary/8 border border-primary/12 flex items-center justify-center shrink-0">
              <span className="text-sm font-bold text-primary">{visita.paziente.avatar}</span>
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-foreground truncate" data-testid="patient-name">
                {visita.paziente.cognome} {visita.paziente.nome}
              </h1>
              <p className="text-xs text-muted-foreground">
                {visita.paziente.eta} anni &middot; {visita.codicePAI} &middot; {visita.orarioInizio}-{visita.orarioFine}
              </p>
            </div>
          </div>
        </div>

        {/* Alert Clinici */}
        <AlertBanner alerts={fascicolo.alertClinici} />

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Column A: PAI Objectives + Today's Tasks */}
          <div className="space-y-5">
            {/* PAI Objective */}
            <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="pai-objectives-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-xl bg-primary/8 flex items-center justify-center">
                    <Target className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-sm font-bold text-foreground">Obiettivo del PAI</h2>
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed" data-testid="pai-objective-text">
                  {fascicolo.obiettivoPAI}
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <User className="w-3 h-3" /> MMG: {fascicolo.mmgRiferimento}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Stethoscope className="w-3 h-3" /> CM: {fascicolo.caseManager}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> PAI: {fascicolo.dataAttivazionePAI} - {fascicolo.scadenzaPAI}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Today's Tasks */}
            <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="today-tasks-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center">
                    <ClipboardList className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-sm font-bold text-foreground">Prestazioni Odierne</h2>
                </div>
                <div className="space-y-2.5">
                  {fascicolo.prestazioniOggi.map((p, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 px-4 py-3 rounded-xl bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/40 dark:border-blue-800/30"
                      data-testid={`today-task-${i}`}
                    >
                      <div className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400">{i + 1}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{p.descrizione}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Codice: {p.codice}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Column B: Shared Diary */}
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="shared-diary-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-xl bg-violet-50 dark:bg-violet-950/30 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                </div>
                <h2 className="text-sm font-bold text-foreground">Diario Condiviso</h2>
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 ml-auto rounded-md text-muted-foreground border-border/40">
                  {fascicolo.diarioCondiviso.length} interventi
                </Badge>
              </div>
              <div>
                {fascicolo.diarioCondiviso.map((entry, i) => (
                  <DiaryEntry
                    key={entry.id}
                    entry={entry}
                    isLast={i === fascicolo.diarioCondiviso.length - 1}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── QR Code Certification Block ─── */}
        <Card
          className={`border-0 rounded-2xl overflow-hidden transition-all duration-500 ${
            presenzaCertificata
              ? 'shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
              : 'shadow-[0_2px_12px_rgba(0,0,0,0.08)]'
          }`}
          data-testid="qr-certification-block"
        >
          {/* Certified Banner (only after QR scan) */}
          <AnimatePresence>
            {presenzaCertificata && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                className="bg-emerald-50 dark:bg-emerald-950/30 border-b border-emerald-200/60 dark:border-emerald-800/40 px-5 py-3 flex items-center gap-3"
                data-testid="certified-banner"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300" data-testid="certified-text">
                    Presenza Certificata alle {oraCertificazione}
                  </p>
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-400">
                    Identita paziente verificata tramite QR Code
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <CardContent className="p-5">
            {!presenzaCertificata ? (
              /* Locked State */
              <div data-testid="diary-locked-state">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl bg-muted/60 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <h2 className="text-sm font-bold text-foreground">Diario Clinico</h2>
                  <Badge variant="outline" className="text-[10px] px-2 py-0.5 ml-auto rounded-md text-orange-600 dark:text-orange-400 border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/30">
                    Bloccato
                  </Badge>
                </div>

                {/* Locked placeholder area */}
                <div className="relative rounded-2xl border-2 border-dashed border-border/50 bg-muted/20 p-8 mb-5">
                  <div className="opacity-25 pointer-events-none space-y-3">
                    <div className="h-4 bg-muted rounded-lg w-3/4" />
                    <div className="h-4 bg-muted rounded-lg w-1/2" />
                    <div className="h-4 bg-muted rounded-lg w-5/6" />
                    <div className="h-10 bg-muted rounded-xl w-full mt-4" />
                    <div className="h-10 bg-muted rounded-xl w-full" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Lock className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground font-medium">Scansiona il QR Code per sbloccare</p>
                    </div>
                  </div>
                </div>

                {/* Big CTA Button */}
                <Button
                  className="w-full h-16 rounded-2xl text-base font-bold gap-3 shadow-lg hover:shadow-xl transition-shadow"
                  onClick={() => setQrModalOpen(true)}
                  data-testid="scan-qr-btn"
                >
                  <Camera className="w-6 h-6" />
                  Scannerizza QR Code Paziente per Iniziare
                </Button>
              </div>
            ) : (
              /* Unlocked State — Clinical Diary Form */
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="diary-unlocked-state"
              >
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center">
                    <Unlock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h2 className="text-sm font-bold text-foreground">Diario Clinico</h2>
                  <Badge variant="outline" className="text-[10px] px-2 py-0.5 ml-auto rounded-md text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30">
                    Sbloccato
                  </Badge>
                </div>

                <ClinicalDiaryForm prestazioni={fascicolo.prestazioniOggi} />
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* QR Scan Modal */}
        <QRScanModal
          isOpen={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          onSuccess={handleScanSuccess}
        />
      </div>
    </AppLayout>
  );
}
